import type { PharosJWTToken } from '@klareospace/pharos-lib-types';
import { decodeJwtPayload, isPharosJWTToken } from '@klareospace/pharos-lib-utils';

/**
 * `PharosJWTToken` with `tenantId` narrowed to a guaranteed string. `isPharosJWTToken` already
 * requires `tenantId` to be a string for a decoded payload to pass, but its declared guard type
 * (`value is PharosJWTToken`) keeps the broader, optional field — callers that need a
 * guaranteed-present `tenantId` use this type instead of re-deriving the same `?? ''` fallback.
 */
export type ResolvedPharosJWTToken = PharosJWTToken & { tenantId: string };

/**
 * Decode a Klareo JWT into its typed Pharos claims, without verifying the signature.
 *
 * Pure function — no React dependency, safe to call from both web and React Native call
 * sites. Returns `null` when `token` is `null`/`undefined`, malformed, or does not carry the
 * minimum Pharos JWT claims (`tenantId`, `userId`) — callers should treat `null` as
 * anonymous/unauthenticated.
 *
 * @param token - Klareo JWT string, or `null`/`undefined` when the user is unauthenticated.
 * @returns The decoded token claims, or `null` when no valid claims can be derived.
 */
export function getPharosTokenClaims(token: string | null | undefined): ResolvedPharosJWTToken | null {
  if (!token) return null;

  const decoded = decodeJwtPayload(token);
  if (!isPharosJWTToken(decoded)) return null;
  // isPharosJWTToken already validated tenantId is a string — narrow the type to match.
  return decoded as ResolvedPharosJWTToken;
}
